import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  Paper,
  Divider,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Send as SendIcon } from "@mui/icons-material";
import axios from "axios";

const CommentSection = ({ postId }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [newComment, setNewComment] = useState({ author_name: "", content: "" });

  useEffect(() => {
    if (postId) {
      fetchComments();
    }
  }, [postId]);

  const fetchComments = async () => {
    try {
      const response = await axios.get(`http://localhost:8000/api/comments/?post_id=${postId}`);
      setComments(response.data.results || response.data);
    } catch (err) {
      console.error("Error fetching comments:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.author_name.trim() || !newComment.content.trim()) return;

    setSubmitting(true);
    setError("");

    try {
      await axios.post("http://localhost:8000/api/comments/", {
        post: postId,
        author_name: newComment.author_name,
        content: newComment.content,
      });
      
      // Reset form and reload comments
      setNewComment({ author_name: "", content: "" });
      fetchComments();
    } catch (err) {
      console.error("Error posting comment:", err);
      if (err.response?.status === 403) {
        setError("Has excedido el límite de comentarios por hora.");
      } else {
        setError("Error al publicar el comentario. Inténtalo de nuevo.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ mt: 6, mb: 4 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Comentarios ({comments.length})
      </Typography>
      <Divider sx={{ mb: 4 }} />

      {/* Comment Form */}
      <Paper elevation={0} sx={{ p: 3, mb: 4, bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Deja un comentario
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Nombre"
            value={newComment.author_name}
            onChange={(e) => setNewComment({ ...newComment, author_name: e.target.value })}
            margin="normal"
            required
            size="small"
          />
          <TextField
            fullWidth
            label="Comentario"
            value={newComment.content}
            onChange={(e) => setNewComment({ ...newComment, content: e.target.value })}
            margin="normal"
            required
            multiline
            rows={3}
          />
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="submit"
              variant="contained"
              endIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              disabled={submitting}
            >
              Publicar Comentario
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Comments List */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {comments.length === 0 ? (
            <Typography color="text.secondary" textAlign="center">
              Sé el primero en comentar.
            </Typography>
          ) : (
            comments.map((comment) => (
              <Paper key={comment.id} elevation={0} sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Avatar sx={{ bgcolor: "primary.main" }}>
                    {comment.author_name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {comment.author_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(comment.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.primary">
                      {comment.content}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))
          )}
        </Box>
      )}
    </Box>
  );
};

export default CommentSection;
